import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import styled from '@emotion/styled';
import WhiteBg from './../assets/white-bg.svg?react';
// Shared WebP, not the old .svg: that file was a base64 PNG in an <svg>
// wrapper (327 kB, zero vector paths) and the same image was duplicated
// as removebg-preview.svg. One 28 kB WebP now serves both.
import trollUrl from 'assets/images/troll-illustration.webp';

const Card = styled('div')(({ theme, background, sx = {}, sm = {} }) => ({
  backgroundColor: background || theme.palette.white.main,
  padding: '24px 32px',
  borderRadius: 24,
  marginTop: 24,
  position: 'relative',
  overflow: 'hidden',
  ...sx,
  [theme.breakpoints.down('sm')]: {
    padding: 24,
    ...sm
  }
}));

const Headline = styled(Typography)(({ theme, mt, mb, fz, ff, mbs, mw }) => ({
  fontSize: fz || 16,
  fontWeight: 500,
  color: theme.palette.grey[600],
  marginTop: mt || 0,
  marginBottom: mb || 0,
  fontFamily: ff || 'Rubik-Regular',
  lineHeight: 'normal',
  maxWidth: mw || '100%',
  '& a': {
    color: theme.palette.tertiary.main
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: mbs || mb || 0
  }
}));

const CardImage = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const WhiteBgStyled = styled(WhiteBg)(() => ({
  position: 'absolute',
  right: 0,
  bottom: 0
}));

const TrollStyled = styled('img')(() => ({
  position: 'absolute',
  right: 0,
  bottom: 0
}));

const CardText = styled('div')(({ mw }) => ({
  maxWidth: mw || 415
}));

// Total number of EncouragementMessageHeaderNN / BodyNN pairs in the
// translations. Currently only ua.json carries variants 02-12; other locales
// have only 01, and missing variants fall back to 01 via defaultValue below.
const ENCOURAGEMENT_VARIANT_COUNT = 12;
const pad2 = (n) => String(n).padStart(2, '0');

const MotivationMessage = ({ fullWidth, brandColor }) => {
  const theme = useTheme();
  const t = useTranslate('Vacancy');
  // Start on variant 01 (SSR/prerender-stable), then randomize after mount —
  // same pattern as vibetown-frontend's MotivationMessage.svelte.
  const [variant, setVariant] = React.useState(1);
  React.useEffect(() => {
    setVariant(Math.floor(Math.random() * ENCOURAGEMENT_VARIANT_COUNT) + 1);
  }, []);
  const headerKey = `EncouragementMessageHeader${pad2(variant)}`;
  const bodyKey = `EncouragementMessageBody${pad2(variant)}`;
  const header = t(headerKey, { defaultValue: t('EncouragementMessageHeader01') });
  const body = t(bodyKey, { defaultValue: t('EncouragementMessageBody01') });
  return (
    <Card
      background={brandColor ? `#${brandColor}` : theme.palette.primary.salad}
      sx={{
        height: 254,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box'
      }}
      sm={{ height: 'auto' }}>
      <CardText mw={fullWidth ? '80%' : 415}>
        <Headline fz={!fullWidth ? 28 : 24} mb={!fullWidth ? 16 : 5} mbs={12} ff={'Unbounded'}>
          {header}
        </Headline>
        <Headline fz={18}>{body}</Headline>
      </CardText>
      <CardImage>
        <WhiteBgStyled />
        <TrollStyled src={trollUrl} alt="" loading="lazy" decoding="async" />
      </CardImage>
    </Card>
  );
};

export default MotivationMessage;
